import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import { useGetCustomersQuery } from '../../redux/apis/customersManagement/customers/cMcustomerApiSlice'
import { toast } from 'react-toastify'
import { Input } from 'antd'
import { Card, CardBody, CardFooter } from '../../components/other/card/card'
import Loader from '../pages/loader'
import { encryptText } from '../../helpers/crypto'

const CustomerDashboard = React.memo(() => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12
  })

  const context = useContext(AppSettings)
  const [searchItem, setSearchItem] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const { data, isLoading, isError, isFetching, error } = useGetCustomersQuery({
    expand: 'media',
    page: pagination.current,
    page_size: pagination.pageSize,
    search: searchQuery || undefined
  })

  const customers = data?.data?.results ?? []
  const totalCount = data?.data?.count ?? 0

  const totalPages = Math.ceil(totalCount / pagination.pageSize)
  const hasNextPage = pagination.current < totalPages
  const hasPrevPage = pagination.current > 1

  const handleNextPage = () => {
    if (hasNextPage && !isFetching) {
      setPagination(prev => ({
        ...prev,
        current: prev.current + 1
      }))
    }
  }

  const handlePrevPage = () => {
    if (hasPrevPage && !isFetching) {
      setPagination(prev => ({
        ...prev,
        current: prev.current - 1
      }))
    }
  }

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  useEffect(() => {
    if (isError) {
      const errorMessage =
        error?.data?.message || error?.message || 'Error loading customers data'
      toast.error(errorMessage)
    }
  }, [isError, error])

  const openCustomerDashboard = customerId => {
    try {
      const uid = encryptText(customerId.toString())
      const token = encryptText(localStorage.getItem('access_token'))

      const dashboardUrl = process.env.REACT_APP_CUSTOMER_DASHBOARD_URL

      if (!dashboardUrl) {
        toast.error('Dashboard URL is not configured')
        return
      }

      console.log(`${dashboardUrl}?uid=${uid}&token=${token}`)
      window.open(`${dashboardUrl}?uid=${uid}&token=${token}`, '_blank')
    } catch (err) {
      toast.error('Failed to open customer dashboard')
      console.error('Error opening customer dashboard:', err)
    }
  }

  const handleSearchChange = e => {
    setSearchItem(e.target.value)
  }

  const handleSearch = () => {
    setSearchQuery(searchItem)
    setPagination(prev => ({
      ...prev,
      current: 1
    }))
  }

  const handleClearSearch = () => {
    setSearchItem('')
    setSearchQuery('')
    setPagination(prev => ({
      ...prev,
      current: 1
    }))
  }

  return (
    <div className=' p-6'>
      <div className=' p-4 rounded-md mb-6 '>
        <div className='flex items-center gap-3'>
          <label
            htmlFor='customerSearch'
            className='text-inverse font-medium whitespace-nowrap'
          >
            Search By Customer Name
          </label>
          <Input
            className='text-inverse font-small'
            size='medium'
            style={{
              width: 204,
              backgroundColor: 'transparent',
              textDecorationColor: 'white'
            }}
            id='customerSearch'
            type='text'
            value={searchItem}
            onChange={handleSearchChange}
            onPressEnter={handleSearch}
            placeholder='Search By Name...'
          />
          <button
            className='btn btn-outline-theme'
            onClick={handleSearch}
            disabled={isFetching}
          >
            Search
          </button>
          {searchQuery && (
            <button
              className='btn btn-outline-secondary'
              onClick={handleClearSearch}
              disabled={isFetching}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {(isLoading || isFetching) && (
        <Loader isLoading={isLoading || isFetching} />
      )}

      <div className='row'>
        {customers.map(customer => (
          <div
            className='col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12 mb-4'
            key={customer?.id}
            onClick={() => openCustomerDashboard(customer?.id)}
            style={{ cursor: 'pointer', height: '100%' }}
          >
            <Card style={{ height: '300px' }}>
              <CardBody style={{ padding: '.2rem', height: '75%' }}>
                {customer?.profile_image ? (
                  <img
                    className='w-full h-full object-contain'
                    src={customer?.profile_image}
                    alt={customer?.customer_name || 'Customer'}
                  />
                ) : (
                  <div className='w-full h-full flex items-center justify-center bg-slate-100 font-bold text-lg text-gray-500'>
                    {customer?.customer_name?.toUpperCase() || 'NO NAME'}
                  </div>
                )}
              </CardBody>
              <CardFooter
                className='text-inverse text-md pt-2 pl-2'
                style={{ height: '25%' }}
              >
                <p>
                  {customer?.customer_name?.toUpperCase() || null} <br />
                  {customer?.customer_business?.toUpperCase() || null}
                </p>
              </CardFooter>
            </Card>
          </div>
        ))}
      </div>

      {customers?.length === 0 && !isLoading && !isFetching && (
        <div className='flex justify-center items-center p-10'>
          <p className='text-lg text-inverse'>
            {searchQuery
              ? 'No customers found matching your search'
              : 'No customers found'}
          </p>
        </div>
      )}

      {isError && (
        <div className='flex justify-center items-center p-10'>
          <div className='text-center'>
            <p className='text-lg text-red-500 mb-4'>
              Failed to load customers
            </p>
            <button
              className='btn btn-outline-theme'
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!isError && totalCount > 0 && (
        <div className='flex justify-center items-center gap-4 mt-6 mb-4'>
          <button
            className='btn btn-outline-theme'
            onClick={handlePrevPage}
            disabled={!hasPrevPage || isFetching}
            style={{ opacity: !hasPrevPage || isFetching ? 0.5 : 1 }}
          >
            Previous
          </button>

          <div className='flex items-center gap-2'>
            <span className='text-inverse'>
              Page {pagination.current} of {totalPages}
            </span>
            <span className='text-inverse text-opacity-50'>
              ({totalCount} total customers)
            </span>
          </div>

          <button
            className='btn btn-outline-theme'
            onClick={handleNextPage}
            disabled={!hasNextPage || isFetching}
            style={{ opacity: !hasNextPage || isFetching ? 0.5 : 1 }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
})

export default CustomerDashboard
